/** After signing: the billing schedule and the file steps; always: the evidence log. */

import { type ContractDetail, type ScheduleLineStatusEnum, useContractsItemsStepCreate } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, type BadgeTone, Button, GlassCard, SelectField } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { localized } from "../../components/TranslatedFields";
import { dateOnly, dateTime, errorText, useRefreshContracts } from "./shared";

const LINE_TONE: Record<ScheduleLineStatusEnum, BadgeTone> = {
  pending: "default",
  invoiced: "offer",
  paid: "success",
  cancelled: "danger",
};

export function Schedule({ contract }: { contract: ContractDetail }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshContracts();
  const step = useContractsItemsStepCreate({ mutation: { onSuccess: refresh } });
  const steps = [...contract.steps].sort((a, b) => a.order - b.order);
  const current = steps.find((s) => s.key === contract.current_step);
  const next = steps.find((s) => s.order > (current?.order ?? -1));
  const [chosen, setChosen] = useState(next?.key ?? steps[0]?.key ?? "");
  const stepName = (key: string) => {
    const found = steps.find((s) => s.key === key);
    return found ? localized(found.name, i18n.language) : key;
  };
  const total = contract.schedule.reduce((sum, line) => sum + line.amount_xaf, 0);
  const invoiced = contract.schedule.filter((l) => l.status === "invoiced" || l.status === "paid").reduce((s, l) => s + l.amount_xaf, 0);

  return (
    <div className="stack">
      <div className="kpis">
        <GlassCard as="div" className="kpi">
          <div className="kpi__link">
            <span className="kpi__label">{t("contracts.schedule.total")}</span>
            <span className="kpi__value">{formatXaf(total)}</span>
          </div>
        </GlassCard>
        <GlassCard as="div" className="kpi">
          <div className="kpi__link">
            <span className="kpi__label">{t("contracts.schedule.invoiced")}</span>
            <span className="kpi__value">{formatXaf(invoiced)}</span>
          </div>
        </GlassCard>
        <GlassCard as="div" className="kpi">
          <div className="kpi__link">
            <span className="kpi__label">{t("contracts.schedule.currentStep")}</span>
            <span className="kpi__value contract-kpi-text">{current ? localized(current.name, i18n.language) : "—"}</span>
          </div>
        </GlassCard>
      </div>
      <GlassCard as="section" className="stack">
        <h2>{t("contracts.schedule.title")}</h2>
        {contract.schedule.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("contracts.schedule.label")}</th>
                  <th className="nowrap">{t("contracts.schedule.amount")}</th>
                  <th>{t("contracts.schedule.trigger")}</th>
                  <th>{t("contracts.schedule.status")}</th>
                  <th>{t("contracts.schedule.invoice")}</th>
                </tr>
              </thead>
              <tbody>
                {contract.schedule.map((line) => (
                  <tr key={line.id}>
                    <td>{line.number}</td>
                    <td>{localized(line.label, i18n.language)}</td>
                    <td className="nowrap">{formatXaf(line.amount_xaf)}</td>
                    <td>
                      {stepName(line.trigger_step)}
                      {line.reached_at ? <span className="muted"> · {dateOnly(line.reached_at, i18n.language)}</span> : null}
                    </td>
                    <td>
                      <Badge tone={LINE_TONE[line.status]}>{t(`contracts.schedule.lineStatus.${line.status}`)}</Badge>
                    </td>
                    <td>
                      {line.invoice_id ? (
                        <Link className="link" to={`/invoices/${line.invoice_id}`}>
                          {line.invoice_number || t("contracts.schedule.openInvoice")}
                        </Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">{t("contracts.schedule.empty")}</p>
        )}
      </GlassCard>
      {steps.length ? (
        <GlassCard as="section" className="stack">
          <h2>{t("contracts.steps.title")}</h2>
          <p className="muted">{t("contracts.steps.help")}</p>
          <ol className="stepper" aria-label={t("contracts.steps.title")}>
            {steps.map((s) => {
              const done = current ? s.order <= current.order : false;
              return (
                <li key={s.key} className={`stepper__step${done ? " stepper__step--done" : s.key === next?.key ? " stepper__step--current" : ""}`}>
                  <span className="muted">{s.order}</span>
                  {localized(s.name, i18n.language)}
                </li>
              );
            })}
          </ol>
          <div className="row contract-code">
            <SelectField
              label={t("contracts.steps.select")}
              value={chosen}
              options={steps.map((s) => ({ value: s.key, label: localized(s.name, i18n.language) }))}
              onChange={(e) => setChosen(e.target.value)}
            />
            <Button disabled={!chosen || step.isPending} onClick={() => step.mutate({ id: contract.id, data: { step: chosen } })}>
              {step.isPending ? t("common.loading") : t("contracts.steps.mark")}
            </Button>
          </div>
          {step.isSuccess ? (
            <p role="status" className="ok">
              {t("contracts.steps.done")}
            </p>
          ) : null}
          {step.isError ? (
            <p role="alert" className="ko">
              {errorText(step.error, t("common.error"))}
            </p>
          ) : null}
        </GlassCard>
      ) : null}
    </div>
  );
}

const DETAIL_KEYS = ["number", "channel", "method", "reason", "step", "template", "parent", "sha256"] as const;

export function EvidenceLog({ contract }: { contract: ContractDetail }) {
  const { t, i18n } = useTranslation();
  const detail = (data: unknown) => {
    const values = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    return DETAIL_KEYS.filter((key) => values[key] !== undefined && values[key] !== "").map((key) => {
      const raw = String(values[key]);
      const value =
        key === "channel"
          ? t(`contracts.channels.${raw}`, { defaultValue: raw })
          : key === "method"
            ? t(`contracts.method.${raw}`, { defaultValue: raw })
            : key === "sha256"
              ? `${raw.slice(0, 16)}…`
              : raw;
      return `${t(`contracts.log.data.${key}`)} : ${value}`;
    });
  };
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("contracts.log.title")}</h2>
      <p className="muted">{t("contracts.log.help")}</p>
      {contract.events.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("contracts.log.date")}</th>
                <th>{t("contracts.log.event")}</th>
                <th>{t("contracts.log.actor")}</th>
                <th>{t("contracts.log.ip")}</th>
                <th>{t("contracts.log.details")}</th>
              </tr>
            </thead>
            <tbody>
              {contract.events.map((event) => (
                <tr key={event.id}>
                  <td className="contract-nowrap">{dateTime(event.created_at, i18n.language)}</td>
                  <td>
                    <Badge tone={event.kind === "signed" ? "success" : event.kind === "cancelled" || event.kind === "otp_failed" ? "danger" : "default"}>
                      {t(`contracts.events.${event.kind}`)}
                    </Badge>
                  </td>
                  <td>{event.actor_name || t("contracts.log.client")}</td>
                  <td>
                    <span title={event.user_agent}>{event.ip ?? "—"}</span>
                  </td>
                  <td className="muted">{detail(event.data).join(" · ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">{t("contracts.log.empty")}</p>
      )}
    </GlassCard>
  );
}
